import { useMemo } from 'react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TooltipFormatterContextObject } from 'highcharts';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getUIConfigForRangeType } from '../../features/activity/utils';
import { selectSite } from '../../features/site/siteSlice';
import { getLocaleForDateFns, SupportedLocales } from '../../utils/common/common-utils';
import { DateRangeType } from '../components/DateRangeTypePicker';

const UNIT_THRESHOLD = 1;
const UNIT_MULTIPLIER = 1000;
const UNIT_DECIMALS = 2;

// Utility to map chart-specific formats to date-fns formats
const DATE_TIME_MAP = new Map<string, string>([
  ['%H', 'HH'], // Hour (24-hour)
  ['%M', 'mm'], // Minute
  ['%l', 'h'], // Hour (12-hour, no leading zero)
  ['%P', 'a'], // AM/PM
  ['%e', 'd'], // Day of the month (no leading zero)
  ['%b', 'MMM'], // Abbreviated month name
  ['%Y', 'yyyy'], // Full year
]);

const convertChartFormatToDateFnsFormat = (chartFormat: string): string =>
  chartFormat.replace(/%[HMPelbY]/g, (match) => DATE_TIME_MAP.get(match) || match);

export function useChartFormatter(selectedDateRangeType: DateRangeType, isSeriesName = false) {
  const { timezone } = useSelector(selectSite);
  const { i18n } = useTranslation();
  const localeForDateFns = getLocaleForDateFns(i18n.language as SupportedLocales);
  const isDayView = selectedDateRangeType === DateRangeType.Day;

  const dateFnsFormat = useMemo(() => {
    const { tooltipDateFormat } = getUIConfigForRangeType(selectedDateRangeType);
    return convertChartFormatToDateFnsFormat(tooltipDateFormat);
  }, [selectedDateRangeType]);

  function energyValueFormatter(value: string | number | null | undefined) {
    if (!value) {
      return '<p style="font-size: 16px;">0</p>';
    }
    const valueNumber = Number(value);
    if (valueNumber < UNIT_THRESHOLD && valueNumber > -UNIT_THRESHOLD) {
      return `${+(Number(valueNumber) * UNIT_MULTIPLIER).toFixed(UNIT_DECIMALS)} ${isDayView ? 'W' : 'Wh'}`;
    }
    return `${+Number(valueNumber).toFixed(UNIT_DECIMALS)} ${isDayView ? 'kW' : 'kWh'}`;
  }

  function dateTooltipFormatter(timestamp: string | number | undefined) {
    switch (selectedDateRangeType) {
      case DateRangeType.Day:
        return formatInTimeZone(new Date(timestamp ?? 0), timezone, dateFnsFormat);
      case DateRangeType.Year:
        return format(new Date(timestamp ?? 0), 'MMMM', { locale: localeForDateFns });
      default:
        return format(new Date(timestamp ?? 0), dateFnsFormat, { locale: localeForDateFns });
    }
  }

  function energyTooltipFormatter(context: TooltipFormatterContextObject) {
    const header = `<span style="font-size: 16px; font-weight: bold;">${dateTooltipFormatter(context.x)}</span><br/>`;
    const points = context.points?.map(
      ({ y, color, series }) =>
        `<span style="color:${color}; font-size: 28px;">∎</span>
      <span style="font-size: 16px;">
        ${isSeriesName ? series.name + ': ' : ''}
        ${energyValueFormatter(y)}
      </span><br/>`
    );
    return [header, ...(points ?? [])];
  }

  return {
    energyValueFormatter,
    dateTooltipFormatter,
    energyTooltipFormatter,
  };
}
